import React from 'react';
import {
  FiX, FiUser, FiPhone, FiMail, FiMapPin, FiHome, FiClock,
  FiUsers, FiStar, FiBriefcase, FiExternalLink, FiMessageCircle,
  FiCalendar, FiRepeat
} from 'react-icons/fi';
import './ModalDetalhesIrmandade.css';

const CAT_LABEL = { crianca: 'Criança', jovem: 'Jovem', adulto: 'Adulto' };

const formatarData = (data) => {
  if (!data) return null;
  return new Date(data).toLocaleDateString('pt-BR');
};

const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nasc = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nasc.getFullYear();
  const mes = hoje.getMonth() - nasc.getMonth();
  if (mes < 0 || (mes === 0 && hoje.getDate() < nasc.getDate())) idade--;
  return idade;
};

const iniciais = (nome) => {
  if (!nome) return '?';
  const partes = String(nome).trim().split(/\s+/);
  const a = partes[0]?.[0] || '';
  const b = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (a + b).toUpperCase();
};

const soDigitos = (tel) => String(tel || '').replace(/\D/g, '');
const linkWhatsApp = (tel) => {
  const d = soDigitos(tel);
  if (!d) return null;
  const numero = d.startsWith('55') ? d : `55${d}`;
  return `https://wa.me/${numero}`;
};

const ModalDetalhesIrmandade = ({ isOpen, onClose, membro }) => {
  if (!isOpen || !membro) return null;

  const idade = calcularIdade(membro.data_nascimento);
  const categoria = membro.categoria;
  const catLabel = CAT_LABEL[categoria] || categoria || '—';
  const ehCrianca = categoria === 'crianca';
  const temCargos = categoria === 'jovem' || categoria === 'adulto';
  const generoLabel = membro.tipo_pessoa === 'irmao' ? 'Irmão'
    : membro.tipo_pessoa === 'irma' ? 'Irmã' : null;

  // Cargos
  const cargos = Array.isArray(membro.irmandade_cargos)
    ? membro.irmandade_cargos.map(ic => ic?.cargo?.nome).filter(Boolean)
    : [];

  // Responsáveis: usa vínculos; fallback para responsavel único ou observações
  let responsaveis = Array.isArray(membro.responsaveis_vinculos)
    ? membro.responsaveis_vinculos
        .map(v => ({
          nome: v.responsavel?.nome_completo,
          telefone: v.responsavel?.telefone,
          principal: !!v.principal,
        }))
        .filter(r => r.nome)
    : [];
  if (responsaveis.length === 0 && membro.responsavel?.nome_completo) {
    responsaveis = [{ nome: membro.responsavel.nome_completo, telefone: null, principal: true }];
  }
  const responsavelObs = responsaveis.length === 0 && membro.observacoes
    ? String(membro.observacoes).replace(/^Respons[aá]vel:\s*/i, '')
    : null;

  const temCoords = membro.latitude != null && membro.longitude != null;
  const mapaUrl = temCoords
    ? `https://www.google.com/maps?q=${membro.latitude},${membro.longitude}`
    : (membro.endereco ? `https://www.google.com/maps?q=${encodeURIComponent(membro.endereco)}` : null);

  const waProprio = linkWhatsApp(membro.telefone);
  const mudouSe = !!membro.mudou_se;

  return (
    <div className="mdi-overlay" onClick={onClose}>
      <div className="mdi-modal" onClick={(e) => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className={`mdi-header cat-${categoria || 'todos'}`}>
          <button className="mdi-close" onClick={onClose} aria-label="Fechar">
            <FiX size={22} />
          </button>
          <div className="mdi-avatar">{iniciais(membro.nome_completo)}</div>
          <div className="mdi-header-info">
            <h2>{membro.nome_completo || 'Sem nome'}</h2>
            <div className="mdi-tags">
              <span className={`mdi-cat-badge cat-${categoria || 'todos'}`}>{catLabel}</span>
              {generoLabel && <span className="mdi-tag">{generoLabel}</span>}
              {idade != null && <span className="mdi-tag">{idade} anos</span>}
              <span className={`mdi-status ${mudouSe ? 'mudou' : 'ativo'}`}>
                {mudouSe ? <><FiRepeat size={12} /> Mudou-se</> : 'Ativo'}
              </span>
            </div>
          </div>
        </div>

        <div className="mdi-body">
          {/* Dados pessoais */}
          <section className="mdi-section">
            <h3><FiUser /> Dados pessoais</h3>
            <div className="mdi-grid">
              <div className="mdi-item">
                <span className="mdi-label"><FiCalendar size={13} /> Nascimento</span>
                <span className="mdi-value">{formatarData(membro.data_nascimento) || 'Não informado'}</span>
              </div>
              <div className="mdi-item">
                <span className="mdi-label">Idade</span>
                <span className="mdi-value">{idade != null ? `${idade} anos` : 'Não informado'}</span>
              </div>
              <div className="mdi-item">
                <span className="mdi-label">Categoria</span>
                <span className="mdi-value">{catLabel}</span>
              </div>
              <div className="mdi-item">
                <span className="mdi-label">Gênero</span>
                <span className="mdi-value">{generoLabel || 'Não informado'}</span>
              </div>
            </div>
          </section>

          {/* Contato */}
          <section className="mdi-section">
            <h3><FiPhone /> Contato</h3>
            <div className="mdi-grid">
              <div className="mdi-item">
                <span className="mdi-label">Telefone</span>
                {membro.telefone ? (
                  <span className="mdi-value mdi-value-acoes">
                    {membro.telefone}
                    <a className="mdi-mini-btn ligar" href={`tel:${soDigitos(membro.telefone)}`} title="Ligar">
                      <FiPhone size={13} />
                    </a>
                    {waProprio && (
                      <a className="mdi-mini-btn wa" href={waProprio} target="_blank" rel="noreferrer" title="WhatsApp">
                        <FiMessageCircle size={13} />
                      </a>
                    )}
                  </span>
                ) : <span className="mdi-value mdi-vazio">Não informado</span>}
              </div>
              <div className="mdi-item">
                <span className="mdi-label"><FiMail size={13} /> Email</span>
                <span className="mdi-value">{membro.email || <span className="mdi-vazio">Não informado</span>}</span>
              </div>
            </div>
          </section>

          {/* Endereço */}
          <section className="mdi-section">
            <h3><FiMapPin /> Endereço</h3>
            <div className="mdi-endereco">
              <span className="mdi-value">{membro.endereco || <span className="mdi-vazio">Não informado</span>}</span>
              {mapaUrl && (
                <a className="mdi-mapa-link" href={mapaUrl} target="_blank" rel="noreferrer">
                  <FiExternalLink size={13} /> {temCoords ? 'Ver localização no mapa' : 'Procurar no mapa'}
                </a>
              )}
            </div>
          </section>

          {/* Responsáveis (crianças) */}
          {(ehCrianca || responsaveis.length > 0 || responsavelObs) && (
            <section className="mdi-section">
              <h3><FiUsers /> {responsaveis.length > 1 ? 'Responsáveis' : 'Responsável'}</h3>
              {responsaveis.length > 0 ? (
                <ul className="mdi-resp-lista">
                  {responsaveis.map((r, i) => {
                    const wa = linkWhatsApp(r.telefone);
                    return (
                      <li key={i} className={`mdi-resp-item${r.principal ? ' principal' : ''}`}>
                        <div className="mdi-resp-nome">
                          {r.principal && <FiStar className="mdi-resp-star" size={14} />}
                          <span>{r.nome}</span>
                          {r.principal && <span className="mdi-resp-badge">Principal</span>}
                        </div>
                        {r.telefone && (
                          <div className="mdi-resp-contato">
                            <span>{r.telefone}</span>
                            <a className="mdi-mini-btn ligar" href={`tel:${soDigitos(r.telefone)}`} title="Ligar">
                              <FiPhone size={12} />
                            </a>
                            {wa && (
                              <a className="mdi-mini-btn wa" href={wa} target="_blank" rel="noreferrer" title="WhatsApp">
                                <FiMessageCircle size={12} />
                              </a>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : responsavelObs ? (
                <p className="mdi-value">{responsavelObs}</p>
              ) : (
                <p className="mdi-vazio">Nenhum responsável vinculado.</p>
              )}
            </section>
          )}

          {/* Cargos (jovens/adultos) */}
          {temCargos && (
            <section className="mdi-section">
              <h3><FiBriefcase /> Cargos</h3>
              {cargos.length > 0 ? (
                <div className="mdi-cargos">
                  {cargos.map((c, i) => <span key={i} className="mdi-cargo-badge">{c}</span>)}
                </div>
              ) : <p className="mdi-vazio">Nenhum cargo.</p>}
            </section>
          )}

          {/* Congregação + Sistema */}
          <section className="mdi-section">
            <h3><FiHome /> Congregação e sistema</h3>
            <div className="mdi-grid">
              <div className="mdi-item">
                <span className="mdi-label">Congregação</span>
                <span className="mdi-value">{membro.congregacao || <span className="mdi-vazio">Não informado</span>}</span>
              </div>
              <div className="mdi-item">
                <span className="mdi-label"><FiClock size={13} /> Cadastro</span>
                <span className="mdi-value">{formatarData(membro.created_at) || '—'}</span>
              </div>
              <div className="mdi-item">
                <span className="mdi-label">Atualização</span>
                <span className="mdi-value">{formatarData(membro.updated_at) || '—'}</span>
              </div>
              {mudouSe && (
                <div className="mdi-item">
                  <span className="mdi-label">Mudou-se em</span>
                  <span className="mdi-value">{formatarData(membro.data_mudou_se) || '—'}</span>
                </div>
              )}
            </div>
          </section>

          {/* Observações */}
          {membro.observacoes && (
            <section className="mdi-section">
              <h3>Observações</h3>
              <p className="mdi-obs">{membro.observacoes}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalDetalhesIrmandade;
